import { useState, useEffect } from 'react';
import { integrationsAPI } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import {
  Mail, Shield, Save, CheckCircle2,
  AlertTriangle, Eye, Plus, X, Loader2, ExternalLink, Plug,
  Users, RefreshCw
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

export default function IntegrationsPage() {
  const [emailConfig, setEmailConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [showEmailKey, setShowEmailKey] = useState(false);
  const [emailApiKey, setEmailApiKey] = useState('');
  // Resend → Contacts sync state
  const [audiences, setAudiences] = useState([]);
  const [loadingAudiences, setLoadingAudiences] = useState(false);
  const [syncStats, setSyncStats] = useState(null);
  const [syncingAll, setSyncingAll] = useState(false);
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    if (!isAdmin) { setLoading(false); return; }
    const fetch = async () => {
      try {
        const eRes = await integrationsAPI.getEmailConfig();
        setEmailConfig(eRes.data);
        // Best-effort load of sync stats (no error if not configured)
        try {
          const stats = await integrationsAPI.resendSyncStats();
          setSyncStats(stats.data);
        } catch {}
      } catch {} finally { setLoading(false); }
    };
    fetch();
  }, [isAdmin]);

  const handleLoadAudiences = async () => {
    setLoadingAudiences(true);
    try {
      // First save API key if user just typed it (otherwise backend has no key)
      if (emailApiKey) {
        const ep = { ...emailConfig, api_key: emailApiKey };
        delete ep.api_key_masked; delete ep.connection_verified; delete ep.verified_at; delete ep.updated_at;
        await integrationsAPI.updateEmailConfig(ep);
        setEmailApiKey('');
      }
      const r = await integrationsAPI.listResendAudiences();
      const loadedAudiences = r.data?.audiences || [];
      setAudiences(loadedAudiences);
      
      // Auto-fix fake or old IDs (like aud_test_12345) that are stuck in the config
      if (emailConfig.resend_audience_id && loadedAudiences.length > 0) {
        if (!loadedAudiences.find(a => a.id === emailConfig.resend_audience_id)) {
          setEmailConfig(p => ({...p, resend_audience_id: '', resend_audience_name: ''}));
        }
      }

      if (!loadedAudiences.length) {
        toast.info('Geen audiences gevonden in Resend. Maak er één aan in je Resend dashboard.');
      } else {
        toast.success(`${loadedAudiences.length} audience(s) geladen`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kon audiences niet ophalen');
    } finally {
      setLoadingAudiences(false);
    }
  };

  const handleSyncAll = async () => {
    if (!window.confirm('Alle leads met e-mailadres synchroniseren naar Resend? Dit kan even duren.')) return;
    setSyncingAll(true);
    try {
      const r = await integrationsAPI.resendSyncAll();
      const s = r.data;
      toast.success(`Sync klaar: ${s.synced} gesynct, ${s.failed} mislukt, ${s.skipped} overgeslagen`);
      // Refresh stats
      try {
        const stats = await integrationsAPI.resendSyncStats();
        setSyncStats(stats.data);
      } catch {}
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Sync mislukt');
    } finally {
      setSyncingAll(false);
    }
  };

  const handleSaveAll = async () => {
    setSaving(true);
    try {
      const ep = { ...emailConfig };
      if (emailApiKey) ep.api_key = emailApiKey;
      delete ep.api_key_masked; delete ep.connection_verified; delete ep.verified_at; delete ep.updated_at;
      await integrationsAPI.updateEmailConfig(ep);

      toast.success('Instellingen opgeslagen');
      const eRes = await integrationsAPI.getEmailConfig();
      setEmailConfig(eRes.data);
      setEmailApiKey('');
      // Refresh sync stats too
      try {
        const stats = await integrationsAPI.resendSyncStats();
        setSyncStats(stats.data);
      } catch {}
    } catch (err) { toast.error(err.response?.data?.detail || 'Opslaan mislukt'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="h-64 flex items-center justify-center"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  if (!isAdmin) {
    return (
      <div className="space-y-5 fade-in" data-testid="integrations-page">
        <h1 className="text-2xl font-bold tracking-tight">Integraties</h1>
        <Card className="border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20">
          <CardContent className="p-8 text-center text-muted-foreground">
            <Shield className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p className="text-sm">Alleen beheerders kunnen integraties beheren.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!emailConfig) return null;

  const VARS = '{lead_name} {agent_name} {company} {date}';

  return (
    <div className="space-y-6 fade-in max-w-4xl" data-testid="integrations-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight">Integraties</h1>
          <p className="text-muted-foreground text-xs sm:text-sm">Berichtkanalen configureren</p>
        </div>
        <Button onClick={handleSaveAll} disabled={saving} data-testid="save-integrations-btn" className="gap-2">
          <Save className="w-4 h-4" /> {saving ? 'Opslaan...' : 'Alles opslaan'}
        </Button>
      </div>

      {/* ─── Email ─── */}
      <ProviderCard
        icon={Mail} iconColor="text-indigo-500" iconBg="bg-indigo-100 dark:bg-indigo-900/30"
        title="E-mail" verified={emailConfig.connection_verified}
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Provider</Label>
            <select
              value={emailConfig.provider || 'sendgrid'}
              onChange={(e) => setEmailConfig(p => ({...p, provider: e.target.value}))}
              className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              data-testid="email-provider-select"
            >
              <option value="sendgrid">SendGrid</option>
              <option value="resend">Resend</option>
            </select>
          </div>
          <div>
            <Label className="text-xs">API Key</Label>
            <div className="relative">
              <Input type={showEmailKey ? 'text' : 'password'} value={emailApiKey} onChange={(e) => setEmailApiKey(e.target.value)} placeholder={emailConfig.api_key_masked || 'Voer API key in'} className="h-9 mono text-xs pr-9" data-testid="email-key-input" />
              <button onClick={() => setShowEmailKey(!showEmailKey)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"><Eye className="w-3.5 h-3.5" /></button>
            </div>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={async () => {
          setTestingEmail(true);
          try {
            const ep = { ...emailConfig };
            if (emailApiKey) ep.api_key = emailApiKey;
            delete ep.api_key_masked; delete ep.connection_verified; delete ep.verified_at; delete ep.updated_at;
            await integrationsAPI.updateEmailConfig(ep);
            const r = await integrationsAPI.testEmailConnection();
            toast.success(`Verbonden via ${r.data.provider}`);
            setEmailConfig(p => ({...p, connection_verified: true}));
            const eRes = await integrationsAPI.getEmailConfig();
            setEmailConfig(eRes.data);
            setEmailApiKey('');
          } catch(e) { toast.error(e.response?.data?.detail || 'Verbinding mislukt'); }
          finally { setTestingEmail(false); }
        }} disabled={testingEmail || (!emailApiKey && !emailConfig.api_key_masked)} className="gap-1.5 h-8 text-xs" data-testid="test-email-btn">
          {testingEmail ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plug className="w-3 h-3" />} Verbinding testen
        </Button>
        <div className="bg-indigo-50 dark:bg-indigo-900/20 rounded-lg p-3 border border-indigo-200 dark:border-indigo-900/40 text-xs text-indigo-700 dark:text-indigo-400">
          {emailConfig.provider === 'sendgrid' ? (
            <>Haal je API key op via <a href="https://app.sendgrid.com/settings/api_keys" target="_blank" rel="noopener noreferrer" className="underline font-medium">SendGrid Instellingen <ExternalLink className="w-3 h-3 inline" /></a></>
          ) : (
            <>Haal je API key op via <a href="https://resend.com/api-keys" target="_blank" rel="noopener noreferrer" className="underline font-medium">Resend Dashboard <ExternalLink className="w-3 h-3 inline" /></a></>
          )}
        </div>
      </ProviderCard>

      {/* ─── Resend → Contacts sync ─── */}
      {emailConfig.provider === 'resend' && (
        <Card className="border-border/60" data-testid="resend-sync-card">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-emerald-100 dark:bg-emerald-900/30">
                  <Users className="w-4 h-4 text-emerald-600" />
                </div>
                <div>
                  <CardTitle className="text-sm">Lead → Resend Contacts sync</CardTitle>
                  <p className="text-[11px] text-muted-foreground mt-0.5">Elke nieuwe lead wordt automatisch een contact in Resend.</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant={emailConfig.resend_sync_enabled ? 'default' : 'secondary'} className="text-[10px]">
                  {emailConfig.resend_sync_enabled ? 'Aan' : 'Uit'}
                </Badge>
                <Switch
                  checked={emailConfig.resend_sync_enabled || false}
                  onCheckedChange={(v) => setEmailConfig(p => ({...p, resend_sync_enabled: v}))}
                  data-testid="resend-sync-toggle"
                />
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Audience picker */}
            <div className="grid grid-cols-1 sm:grid-cols-[1fr,auto] gap-2 items-end">
              <div>
                <Label className="text-xs">Resend Audience</Label>
                {audiences.length > 0 ? (
                  <select
                    value={emailConfig.resend_audience_id || ''}
                    onChange={(e) => {
                      const id = e.target.value;
                      const a = audiences.find(x => x.id === id);
                      setEmailConfig(p => ({...p, resend_audience_id: id, resend_audience_name: a?.name || ''}));
                    }}
                    className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                    data-testid="resend-audience-select"
                  >
                    <option value="">— Kies een audience —</option>
                    {audiences.map(a => (
                      <option key={a.id} value={a.id}>{a.name}</option>
                    ))}
                  </select>
                ) : (
                  <Input
                    value={emailConfig.resend_audience_name || emailConfig.resend_audience_id || ''}
                    readOnly
                    placeholder="Klik 'Audiences laden' om je Resend audiences op te halen"
                    className="text-xs h-9"
                  />
                )}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleLoadAudiences}
                disabled={loadingAudiences || (!emailApiKey && !emailConfig.api_key_masked)}
                className="gap-1.5 h-9 text-xs"
                data-testid="resend-load-audiences-btn"
              >
                {loadingAudiences ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                Audiences laden
              </Button>
            </div>

            {/* Stats */}
            {syncStats && (
              <div className="grid grid-cols-3 gap-2">
                <div className="bg-muted/40 rounded-lg p-3 text-center">
                  <div className="text-lg font-bold">{syncStats.total_leads_with_email || 0}</div>
                  <div className="text-[10px] text-muted-foreground uppercase tracking-wide">Leads met e-mail</div>
                </div>
                <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-lg p-3 text-center">
                  <div className="text-lg font-bold text-emerald-700 dark:text-emerald-400">{syncStats.synced || 0}</div>
                  <div className="text-[10px] text-muted-foreground uppercase tracking-wide">Gesynct</div>
                </div>
                <div className={`rounded-lg p-3 text-center ${(syncStats.failed||0) > 0 ? 'bg-red-50 dark:bg-red-900/20' : 'bg-muted/40'}`}>
                  <div className={`text-lg font-bold ${(syncStats.failed||0) > 0 ? 'text-red-700 dark:text-red-400' : ''}`}>{syncStats.failed || 0}</div>
                  <div className="text-[10px] text-muted-foreground uppercase tracking-wide">Mislukt</div>
                </div>
              </div>
            )}

            {/* Bulk sync button */}
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleSyncAll}
                disabled={syncingAll || !emailConfig.resend_audience_id || !emailConfig.resend_sync_enabled}
                className="gap-1.5 h-8 text-xs"
                data-testid="resend-sync-all-btn"
              >
                {syncingAll ? <Loader2 className="w-3 h-3 animate-spin" /> : <Users className="w-3 h-3" />}
                Sync alle bestaande leads
              </Button>
              {syncStats?.last_bulk_sync?.completed_at && (
                <span className="text-[10px] text-muted-foreground">
                  Laatste bulk: {new Date(syncStats.last_bulk_sync.completed_at).toLocaleString('nl-NL')}
                </span>
              )}
            </div>

            {/* Error details from last bulk sync */}
            {syncStats?.last_bulk_sync?.errors?.length > 0 && (
              <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-3 border border-red-200 dark:border-red-900/40 space-y-1.5">
                <p className="text-xs font-semibold text-red-700 dark:text-red-400">
                  Foutmeldingen laatste sync ({syncStats.last_bulk_sync.errors.length} getoond):
                </p>
                {syncStats.last_bulk_sync.errors.map((err, i) => (
                  <p key={i} className="text-[10px] text-red-600 dark:text-red-300 font-mono break-all leading-relaxed">
                    {err}
                  </p>
                ))}
              </div>
            )}

            <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-lg p-3 border border-emerald-200 dark:border-emerald-900/40 text-xs text-emerald-700 dark:text-emerald-400">
              <strong>Hoe het werkt:</strong> Vanaf het moment dat sync aan staat en een audience is gekozen, wordt elke nieuwe lead (handmatig, via forms, ebook library, workshop builder, capture, etc.) automatisch toegevoegd als contact in Resend. Bij verwijderen van een lead wordt het contact ook verwijderd.
            </div>
          </CardContent>
        </Card>
      )}

      <ChannelConfig
        icon={Mail} iconColor="text-indigo-500" title="E-mail"
        enabled={emailConfig.email_enabled} onToggle={(v) => setEmailConfig(p => ({...p, email_enabled: v}))}
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Van e-mailadres</Label>
            <Input value={emailConfig.from_email || ''} onChange={(e) => setEmailConfig(p => ({...p, from_email: e.target.value}))} placeholder="hallo@jouwbedrijf.nl" className="text-xs h-9" data-testid="email-from-input" />
          </div>
          <div>
            <Label className="text-xs">Van naam</Label>
            <Input value={emailConfig.from_name || ''} onChange={(e) => setEmailConfig(p => ({...p, from_name: e.target.value}))} placeholder="Zynq" className="text-xs h-9" data-testid="email-from-name" />
          </div>
        </div>
        <TemplateList
          templates={emailConfig.templates || []}
          onAdd={() => setEmailConfig(p => ({...p, templates: [...(p.templates||[]), {id:`t_${Date.now()}`, name:'Nieuw sjabloon', subject:'Onderwerp - {company}', body:'Hoi {lead_name},\n\n'}]}))}
          onUpdate={(i, f, v) => { const t = [...emailConfig.templates]; t[i] = {...t[i], [f]: v}; setEmailConfig(p => ({...p, templates: t})); }}
          onRemove={(i) => setEmailConfig(p => ({...p, templates: p.templates.filter((_,j) => j!==i)}))}
          varsHint={VARS} hasSubject={true}
        />
      </ChannelConfig>
    </div>
  );
}


/* ─── Provider Card ─── */
function ProviderCard({ icon: Icon, iconColor, iconBg, title, verified, children }) {
  return (
    <Card className="border-border/60">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${iconBg || 'bg-muted'}`}>
              <Icon className={`w-4 h-4 ${iconColor}`} />
            </div>
            <CardTitle className="text-sm">{title}</CardTitle>
          </div>
          {verified ? (
            <Badge className="bg-green-500 text-white text-[10px] gap-1"><CheckCircle2 className="w-3 h-3" />Verbonden</Badge>
          ) : (
            <Badge variant="secondary" className="text-[10px] gap-1 text-amber-600"><AlertTriangle className="w-3 h-3" />Niet geverifieerd</Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">{children}</CardContent>
    </Card>
  );
}


/* ─── Channel Config Card ─── */
function ChannelConfig({ icon: Icon, iconColor, title, enabled, onToggle, children }) {
  return (
    <Card className="border-border/60">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2"><Icon className={`w-4 h-4 ${iconColor}`} />{title}</CardTitle>
          <div className="flex items-center gap-2">
            <Badge variant={enabled ? 'default' : 'secondary'} className="text-[10px]">{enabled ? 'Ingeschakeld' : 'Uitgeschakeld'}</Badge>
            <Switch checked={enabled || false} onCheckedChange={onToggle} />
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">{children}</CardContent>
    </Card>
  );
}


/* ─── Template List ─── */
function TemplateList({ templates, onAdd, onUpdate, onRemove, varsHint, hasSubject }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <Label className="text-xs font-semibold">Berichtsjablonen</Label>
        <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={onAdd}><Plus className="w-3 h-3" />Toevoegen</Button>
      </div>
      <div className="space-y-3">
        {templates.map((tmpl, i) => (
          <div key={i} className="border rounded-lg p-3 space-y-2 bg-muted/10 group">
            <div className="flex items-center gap-2">
              <Input value={tmpl.name} onChange={(e) => onUpdate(i, 'name', e.target.value)} className="h-7 text-xs font-medium flex-1" />
              <Button variant="ghost" size="sm" className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100" onClick={() => onRemove(i)}><X className="w-3 h-3 text-muted-foreground" /></Button>
            </div>
            {hasSubject && <Input value={tmpl.subject || ''} onChange={(e) => onUpdate(i, 'subject', e.target.value)} className="h-7 text-xs" placeholder="E-mailonderwerp..." />}
            <Textarea value={tmpl.body} onChange={(e) => onUpdate(i, 'body', e.target.value)} rows={hasSubject ? 4 : 2} className="text-xs" />
            <p className="text-[9px] text-muted-foreground">Variabelen: {varsHint}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
